import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { NS } from '../U2106';
import {
  doGetProfile,
  doGetSecretKey,
  doUpdateProfile,
  luHandleAppiError,
  u2106FormRowToUpdatePayload,
  type U2106HourBanks,
  type U2106ProfileFormRow,
  type U2106ProfileResult,
  type U2106EmployeeProfile,
} from '../U2106Service';

export type UseU2106ProfileHandlersReturn = {
  loading: boolean;
  profile: U2106ProfileResult | null;
  hourBanks: U2106HourBanks | null;
  employeeMeta: Pick<U2106EmployeeProfile, 'codeEmployee' | 'numEmployee'> | null;
  loadProfile: () => Promise<U2106ProfileResult | null>;
  saveProfile: (row: U2106ProfileFormRow) => Promise<U2106ProfileResult | null>;
  revealSecretKey: () => Promise<string | null>;
};

export function useU2106ProfileHandlers(): UseU2106ProfileHandlersReturn {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const { updateSessionUser } = useU2000AuthContext();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<U2106ProfileResult | null>(null);

  const loadProfile = useCallback(async (): Promise<U2106ProfileResult | null> => {
    setLoading(true);
    try {
      const response = await doGetProfile(axios);
      const result = response.data.result;
      setProfile(result);
      return result;
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else {
        toast.error(t('myProfile.loadError', { ns: NS }));
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [axios, t, toast]);

  const saveProfile = useCallback(
    async (row: U2106ProfileFormRow): Promise<U2106ProfileResult | null> => {
      setLoading(true);
      try {
        const hasEmployee = profile?.hasEmployee === true;
        const payload = u2106FormRowToUpdatePayload(row, hasEmployee);
        const response = await doUpdateProfile(axios, payload);
        const result = response.data.result;
        setProfile(result);
        toast.success(t('myProfile.success', { ns: NS }));

        // Session = identité usager (pas les noms employé).
        updateSessionUser({
          firstName: result.userFirstName,
          lastName: result.userLastName,
          email: result.userEmail,
        });

        return result;
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        } else {
          toast.error(t('myProfile.errors.generic', { ns: NS }));
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [axios, profile?.hasEmployee, t, toast, updateSessionUser],
  );

  const revealSecretKey = useCallback(async (): Promise<string | null> => {
    try {
      const response = await doGetSecretKey(axios);
      return response.data.result.secretKey ?? null;
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else {
        toast.error(t('myProfile.secretKeyLoadError', { ns: NS }));
      }
      return null;
    }
  }, [axios, t, toast]);

  const hourBanks = profile?.hourBanks ?? null;
  const employeeMeta =
    profile?.employee != null
      ? { codeEmployee: profile.employee.codeEmployee, numEmployee: profile.employee.numEmployee }
      : null;

  return { loading, profile, hourBanks, employeeMeta, loadProfile, saveProfile, revealSecretKey };
}
